import { asyncMap, FINSEMBLECONFIG, getConfigurationAndCache } from "./common/windowFunctions";
let shouldPersistFDC3ContextMemoize = null;
let glue;
let logger;
const shouldPersistFDC3Context = async (glue, logger) => {
    if (shouldPersistFDC3ContextMemoize !== null)
        return shouldPersistFDC3ContextMemoize;
    const manifest = await glue.contexts.get(FINSEMBLECONFIG);
    const manifestShouldPersistFDC3Context = manifest?.iocd?.persistFDC3Context;
    const appConfig = await getConfigurationAndCache(glue.windows.my());
    const appConfigShouldPersistFDC3Context = appConfig?.customProperties?.persistFDC3Context;
    shouldPersistFDC3ContextMemoize = appConfigShouldPersistFDC3Context ?? manifestShouldPersistFDC3Context ?? true;
    logger.debug("FDC3 context persistence enabled?", shouldPersistFDC3ContextMemoize);
    return shouldPersistFDC3ContextMemoize;
};
const persistContext = async (glue, context, logger) => {
    if (!context)
        return;
    if (!await shouldPersistFDC3Context(glue, logger))
        return;
    const name = `#finsemble-persistedContext-${context.type}`;
    logger.debug("Persisting FDC3 context", name, context);
    await glue.windows.my()?.updateContext({
        [name]: context
    });
};
const maybeRestorePersistedContext = async (glue, contextType, handler, logger) => {
    if (!await shouldPersistFDC3Context(glue, logger))
        return;
    const windowContext = await glue.windows.my()?.getContext() ?? {};
    const name = `#finsemble-persistedContext-${contextType}`;
    const maybePersistedData = windowContext[name] ?? windowContext["#finsemble-componentState"]?.[name];
    if (maybePersistedData)
        logger.debug("Restoring FDC3 context", name, maybePersistedData);
    if (maybePersistedData) {
        await handler(maybePersistedData);
    }
};
const queue = [];
var Direction;
(function (Direction) {
    Direction["Both"] = "Both";
    Direction["Listen"] = "Listen";
    Direction["Broadcast"] = "Broadcast";
})(Direction || (Direction = {}));
;
let linkState = {
    channels: [],
    allChannels: [],
    channelDirections: {}
};
let stateChangeHandlers = [];
const convertEntryToDirection = (entry) => {
    if (entry.read && entry.write)
        return "Both";
    if (entry.read)
        return "Listen";
    if (entry.write)
        return "Broadcast";
    return "";
};
const getLinkState = async () => {
    const channels = await glue.channels.myChannels();
    const allChannels = await glue.channels.all();
    const restrictedChannels = (await glue.channels.getRestrictions()).channels;
    return {
        channels: await asyncMap(channels, (channel) => fdc3.getOrCreateChannel(channel)),
        allChannels: await asyncMap(allChannels, (channel) => fdc3.getOrCreateChannel(channel)),
        channelDirections: Object.fromEntries(restrictedChannels.map((entry) => [entry.name, convertEntryToDirection(entry)]))
    };
};
const runLinkStateRecipe = () => {
    getLinkState().then((newLinkState) => linkState = newLinkState);
    glue.channels.onChanged(async () => {
        linkState = await getLinkState();
        stateChangeHandlers.forEach((handler) => handler(null, linkState));
    });
    glue.windows.my()?.onChannelRestrictionsChanged(async () => {
        linkState = await getLinkState();
        stateChangeHandlers.forEach((handler) => handler(null, linkState));
    });
};
export const monkeyPatchFDC3Initialize = () => {
    if (!globalThis.fdc3) {
        console.log("No fdc3 object found on global path. Skipping monkey patch.");
        return;
    }
    const originalAddContextListener = fdc3.addContextListener;
    const originalBroadcast = fdc3.broadcast;
    const originalAddIntentListener = fdc3.addIntentListener;
    const originalRaiseIntent = fdc3.raiseIntent;
    const originalOpen = fdc3.open;
    const originalGetSystemChannels = fdc3.getUserChannels;
    fdc3.addContextListener = async (contextType, handler) => {
        (logger ? logger : console).log("intercepted addContextListener", contextType);
        if (!handler) {
            handler = contextType;
            contextType = null;
        }
        const realHandler = handler ?? contextType;
        const substituteHandler = (context, metadata) => {
            persistContext(glue, context, logger);
            return realHandler(context, metadata);
        };
        if (typeof contextType === "string") {
            if (glue) {
                await maybeRestorePersistedContext(glue, contextType, realHandler, logger);
            }
            else {
                queue.push(() => {
                    maybeRestorePersistedContext(glue, contextType, realHandler, logger);
                });
            }
        }
        if (handler) {
            return await originalAddContextListener(contextType, substituteHandler);
        }
        else {
            return await originalAddContextListener(substituteHandler);
        }
    };
    fdc3.broadcast = async (context) => {
        if (!glue) {
            queue.push(() => {
                fdc3.broadcast(context);
            });
            return;
        }
        persistContext(glue, context, logger);
        return await originalBroadcast(context);
    };
    fdc3.addIntentListener = async (intent, handler) => {
        const substituteHandler = async (context, metadata) => {
            persistContext(glue, context, logger);
            return await handler(context, metadata);
        };
        return await originalAddIntentListener(intent, substituteHandler);
    };
    fdc3.leaveChannel = async (channelId) => {
        if (!glue) {
            return;
        }
        await glue.channels.leave(glue.windows.my()?.id, channelId);
    };
    fdc3.getCurrentChannels = async () => {
        if (!glue) {
            return null;
        }
        const current = glue.channels.current() ?? "";
        const joinedChannels = current ? current.split("+++") : [];
        const allChannels = await fdc3.getUserChannels();
        return allChannels.filter((channel) => joinedChannels.includes(channel.id));
    };
    const nameToAppId = async (app) => {
        let realApp = app;
        const maybeName = typeof (app) === "string" ? app : (app?.name ? app.name : undefined);
        if (maybeName) {
            const applications = await glue.appManager.applications();
            const lookupApp = applications.find((a) => a.userProperties?.appd?.name === maybeName);
            if (lookupApp) {
                realApp = {
                    appId: lookupApp.name
                };
            }
        }
        return realApp;
    };
    fdc3.getSystemChannels = async () => {
        const channels = await originalGetSystemChannels();
        return channels.sort((a, b) => {
            if (a.id < b.id)
                return -1;
            if (a.id > b.id)
                return 1;
            return 0;
        });
    };
    fdc3.getUserChannels = async () => {
        const channels = await originalGetSystemChannels();
        return channels.sort((a, b) => {
            if (a.id < b.id)
                return -1;
            if (a.id > b.id)
                return 1;
            return 0;
        });
    };
    fdc3.raiseIntent = async (intent, context, app) => {
        return originalRaiseIntent(intent, context, await nameToAppId(app));
    };
    fdc3.open = async (app, context) => {
        return originalOpen(await nameToAppId(app), context);
    };
    fdc3.linkToChannel = (channelName, direction, windowName) => {
        glue.channels.join(channelName, windowName ?? undefined).then(() => {
            if (direction === "Listen" || direction === "Broadcast") {
                glue.channels.restrict({
                    name: channelName,
                    read: direction === "Listen",
                    write: direction === "Broadcast"
                });
            }
        });
        if (!window) {
            const channel = linkState.allChannels.find((channel) => channel.id === channelName);
            if (channel)
                linkState.channels.push(channel);
            linkState.channelDirections[channelName] = direction;
        }
        return linkState;
    };
    fdc3.getLinkedChannels = () => {
        return linkState.channels.map((channel) => channel.id);
    };
    fdc3.onStateChange = (cb) => {
        stateChangeHandlers.push(cb);
    };
    fdc3.getChannelDirections = () => {
        return linkState.channelDirections;
    };
    fdc3.addChannelListener = (contextType, handler) => {
        return fdc3.addContextListener(contextType, async (context) => {
            const linkState = await getLinkState();
            handler(context, {
                channels: Object.entries(linkState.channelDirections).filter((entry) => entry[1] === "Both" || entry[1] === "Listen").map((entry) => entry[0]),
                allChannels: linkState.allChannels
            });
        });
    };
    fdc3.publishToChannel = (context) => {
        fdc3.broadcast(context);
    };
    fdc3.unlinkFromChannel = (channelName, windowName, doNotApplyAgain) => {
        linkState.channels = linkState.channels.filter((channel) => channel.id !== channelName);
        glue.channels.leave({ windowId: windowName ?? undefined, channel: channelName });
    };
};
export const monkeyPatchFDC3Start = (localGlue, localLogger) => {
    if (!globalThis.fdc3) {
        return;
    }
    glue = localGlue;
    logger = localLogger;
    runLinkStateRecipe();
    logger.debug("Monkey patching fdc3. Queued broadcasts:", queue.length);
    queue.forEach((func) => {
        func();
    });
};
